import React, { useState, useContext, useEffect } from 'react';
import cn from 'classnames';
import ReactHintFactory from 'react-hint';
import Slider from 'react-slick';
import Select from 'react-select';
import Link from 'next/link';
import { getFreeSmsPerMonth } from 'components/utils/pricing/calculator';
import PlanCalculatorDetail from './plan-calculator-detail';
import { TimelyPlanName } from '../utils/pricing/TimelyPlanName';
import { getFixedMonthlyPrice, getRegionPriceInfo, getPlanMonthlyPrice } from '../utils/pricing/calculator';
import { PageContext } from '../context/page-context';
import { MediaServerRender, ProvideMediaMatchers, MediaMatcher, useMedia } from 'react-media-match';

// Mini Pricing cards block

const ReactHint = ReactHintFactory(React);

export default function PlanCalculator({ isDetail = false, className = '' }) {
    const context = useContext(PageContext);
    const { geolocation, setPageContext, businessSize } = context;

    const [staffCount, setCount] = useState(1);

    useEffect(() => {
        let defaultStaffCount = 1;
        if (businessSize && typeof context.staffCount === 'undefined') {
            if (businessSize == '2') {
                defaultStaffCount = 2;
            }
            if (businessSize == '3') {
                defaultStaffCount = 8;
            }
            if (businessSize == '4') {
                defaultStaffCount = 20;
            }
        }

        if (context.staffCount) {
            defaultStaffCount = context.staffCount;
        }

        setCount(defaultStaffCount);
    }, [businessSize]);

    const setStaffCount = (val) => {
        const staffCountNumber = Number(val);
        let businessS = 1;
        if (staffCountNumber > 1) {
            businessS = 2;
        }
        if (staffCountNumber > 7) {
            businessS = 3;
        }
        if (staffCountNumber > 19) {
            businessS = 4;
        }
        setCount(staffCountNumber);
        context.staffCount = staffCountNumber;
        setPageContext('businessSize', businessS, { ...context });
        setPageContext('staffCount', staffCountNumber, { ...context });
    };

    if (isDetail) {
        return <PlanCalculatorDetail />;
    }

    return (
        <div className={`${className} PlanCalculator-wrap PlanCalculator--inline`}>
            <div className="container">
                <span className="PricingCard-tagline my-4">Our plans &amp; pricing</span>

                <div className="PricingCard-heading pt-4 u-vSpacingSM-bottom">
                    <h2 className="PricingCard-heading-title">Find a plan for</h2>
                    <div className="col-10 col-md-5 mx-auto mx-md-0">
                        <StaffSelector staffCount={staffCount} setStaffCount={setStaffCount} />
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="col-xs-12 col-lg-offset-1 col-lg-10 mb-4">
                    <div className="container--fullHeight">
                        <ProvideMediaMatchers>
                            <MediaServerRender predicted="desktop" hydrated={true}>
                                <MediaMatcher
                                    mobile={<PlanCarousel geolocation={geolocation} staffCount={staffCount} />}
                                    tablet={<PlanList geolocation={geolocation} staffCount={staffCount} />}
                                />
                            </MediaServerRender>
                        </ProvideMediaMatchers>

                    </div>
                </div>
            </div>
            <p className="u-textCenter u-vSpacingSM-top">
                <Link href='/pricing/'><a className="Button Button--link" >See full pricing details</a>
                </Link></p>
        </div>
    );
}

function PlanList({ geolocation, staffCount }) {
    return (
        <>
            <PricingCard
                type={TimelyPlanName.BUILD}
                geolocation={geolocation}
                name="Build"
                description="Make business easy with the foundations for managing."
                staffCount={staffCount}
            />
            <PricingCard
                type={TimelyPlanName.ELEVATE}
                geolocation={geolocation}
                name="Elevate"
                description="Grow your revenue and elevate your brand with Timely."
                staffCount={staffCount}
            />
            <PricingCard
                type={TimelyPlanName.INNOVATE}
                geolocation={geolocation}
                description="Access sophisticated tools to innovate and grow."
                name="Innovate"
                staffCount={staffCount}
            />
        </>
    );
}
function PlanCarousel({ geolocation, staffCount }) {
    /** Convert a plan name to title case */
    function planTitleCase(str) {
        const first = str[0].toUpperCase();
        const rest = str.substring(1).toLowerCase();
        return first + rest;
    }

    const planCardDetails = [
        {
            type: TimelyPlanName.BUILD,
            name: planTitleCase(TimelyPlanName.BUILD),
            description: 'For salons who want to simplify their day to day.',
        },
        {
            type: TimelyPlanName.ELEVATE,
            name: planTitleCase(TimelyPlanName.ELEVATE),
            description: 'Boost your brand, reduce admin, and sync with your business tools.',
        },
        {
            type: TimelyPlanName.INNOVATE,
            name: planTitleCase(TimelyPlanName.INNOVATE),
            description: 'Revolutionise your salon and enhance your client experience.',
        },
    ];
    const defaultPlan = TimelyPlanName.ELEVATE;
    const initialSlide = planCardDetails.findIndex((slide) => slide.type === defaultPlan);

    const settings = {
        dots: true,
        infinite: false,
        initialSlide,
        slidesToShow: 1,
        slidesToScroll: 1
    };
    return (
        <Slider {...settings} >
            {planCardDetails.map((planContent, i) => (
                <PricingCard
                    key={i}
                    geolocation={geolocation}
                    staffCount={staffCount}
                    {...planContent}
                />
            ))}
        </Slider>
    );
}

function PricingCard({ geolocation, staffCount, name, description, type }) {
    const regionPriceInfo = getRegionPriceInfo(geolocation);
    const { currencyCode, sign } = regionPriceInfo;
    const hasFixedPriceOffering = !!getFixedMonthlyPrice(geolocation, type);
    const hasCustomPackageOffering = regionPriceInfo.prices.customPackages[type];

    let totalPrice = getPlanMonthlyPrice(geolocation, type, staffCount) ?? 0;
    
    // 20+ view. Greater than or equal to 20
    if (staffCount >= 20) {
        if (hasCustomPackageOffering) {
            return (
                <div className={`col-12 col-md-4 px-3`}>
                    <div className={`PricingCard PricingCard--${type}`}>
                        <h3 className="PricingCard-title">{name}</h3>
                        <p className="PricingCard-description">{description}</p>
                        <div className="PricingCard-min-height">
                            <h3 className="PricingCard-title">Custom pricing</h3>
                            <div className="PricingCard-staffPrice">Tailored to your needs</div>
                        </div>
                        <button className="btn-nav-call Button Button--full Button--midnight">
                            Book a call
                        </button>
                    </div>
                </div>
            );
        } else if (hasFixedPriceOffering) {
            return (
                <div className={`col-12 col-md-4 px-3`}>
                    <div className={`PricingCard PricingCard--${type} PricingCard--disabled`}>
                        <h3>{name}</h3>
                        <p className="PricingCard-description">
                            Custom packages are not available on this plan.
                        </p>
                    </div>
                </div>
            );
        }
    }

    // Disabled view build 8-20 & 20+
    if (staffCount > 7 && !hasFixedPriceOffering) {
        return (
            <div className={`col-12 col-md-4 px-3`}>
                <div className={`PricingCard PricingCard--${type} PricingCard--disabled`}>
                    <h3>{name}</h3>
                    <p className="PricingCard-description">{description}</p>
                    <p className="PricingCard-description-disabled">This plan is only available for businesses with 1-7 staff.</p>
                    <button className="btn-nav-call Button Button--full Button--midnight" disabled>Book a call</button>
                </div>
            </div>
        );
    }

    // All remaining cards 
    return (
        <div className={`col-12 col-md-4 px-3`}>
            <div className={cn(`PricingCard PricingCard--${type}`)}>
                <h3 className="PricingCard-title">{name}</h3>
                <p className="PricingCard-description">{description}</p>
                <div className="PricingCard-price">
                    <div className='Pricing-price PricingCard-priceBlock'>
                        <div className='Pricing-symbol'>{sign}</div>
                        <div className='Pricing-cost'>
                            <div className='Pricing-amount'>
                                {totalPrice}
                            </div>
                        </div>
                    </div>
                    <div className='Pricing-recurrence'>
                        <span className='Pricing-currency'>{currencyCode}</span>{" / "}
                        <span className='Pricing-recurrence-amount'>mo</span>
                    </div>
                </div>

                {staffCount === 8 ?
                    <span className="PricingCard-sub-line"><span className='bold'>{getFreeSmsPerMonth(geolocation, type, 1)} SMS</span> per staff per month</span> 
                :
                    <span className="PricingCard-sub-line"><span className='bold'>{getFreeSmsPerMonth(geolocation, type, staffCount)} SMS</span> per month included</span>
                }
                <span className="PricingCard-sub-line">No new client fees</span>
                <a href={`https://app.gettimely.com/register?plan=${type}`} className="Button Button--full Button--midnight">
                    Start a free trial
                </a>
            </div>
        </div>

    );
}
function StaffSelector({ staffCount, setStaffCount }) {

    const position = useMedia({
        mobile: 'bottom',
        tablet: 'auto',
    });
    const autoPosition = (position === 'auto');
    const options = [
        { value: '1', label: 'Just me' },
        { value: '2', label: '2 bookable staff' },
        { value: '3', label: '3 bookable staff' },
        { value: '4', label: '4 bookable staff' },
        { value: '5', label: '5 bookable staff' },
        { value: '6', label: '6 bookable staff' },
        { value: '7', label: '7 bookable staff' },
        { value: '8', label: '8-20 bookable staff' },
        { value: '20', label: '20+ bookable staff' },
    ];
    const defaultValue = options.find(o => o.value == staffCount);
    return (
        <>
            <ReactHint
                autoPosition={autoPosition}
                position={position}
                attribute="data-plan"
                events={{ click: false, hover: true }}
                onRenderContent={(target, content) => {
                    return (
                        <div
                            className='react-hint__content'
                            dangerouslySetInnerHTML={{ __html: content }}
                        />
                    );
                }}
            />
            <div data-plan="Bookable staff are anyone in your business that has appointments with clients. Admin staff are not billed and part-time staff may recieve special pricing.">
                <Select
                    className="PlanCalculator-select"
                    classNamePrefix="PlanCalculator-select"
                    theme={theme => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            primary75: '#fc5f64',
                            primary50: '#fc5f64',
                        },
                    })}
                    isSearchable={false}
                    onChange={(e) => setStaffCount(e.value)}
                    defaultValue={defaultValue}
                    value={defaultValue}
                    name="staffCount"
                    options={options}
                />
            </div>
        </ >
    );
}
